/**
 * Focus restore for controlled Radix dialogs (SH-5).
 *
 * Radix returns focus to a `Dialog.Trigger` on close. Almost every dialog in
 * the app is controlled (`open` state set by a plain button) and has no
 * Trigger, so focus fell to <body> and keyboard users lost their place.
 * Spread the returned handlers on Dialog.Content: the element focused when the
 * dialog opened is refocused when it closes, unless the caller's own
 * handlers already handled the event.
 */
import { useCallback, useRef } from 'react';

export function useReturnFocusOnClose(
  onOpenAutoFocus?: (event: Event) => void,
  onCloseAutoFocus?: (event: Event) => void
): { onOpenAutoFocus: (event: Event) => void; onCloseAutoFocus: (event: Event) => void } {
  const returnTo = useRef<HTMLElement | null>(null);
  const handleOpen = useCallback(
    (event: Event) => {
      // Fires before Radix moves focus into the content.
      returnTo.current = document.activeElement as HTMLElement | null;
      onOpenAutoFocus?.(event);
    },
    [onOpenAutoFocus]
  );
  const handleClose = useCallback(
    (event: Event) => {
      onCloseAutoFocus?.(event);
      if (event.defaultPrevented) return;
      const target = returnTo.current;
      if (target && target !== document.body && target.isConnected) {
        event.preventDefault();
        target.focus();
      }
    },
    [onCloseAutoFocus]
  );
  return { onOpenAutoFocus: handleOpen, onCloseAutoFocus: handleClose };
}
